import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
import { Subscription } from 'rxjs'
import { filter, tap } from 'rxjs/operators'
import { SeFeToastService } from 'se-fe-toast'
import { Persona, User } from '../../models'
import { SpinnerService, UserService } from '../../services'
import { ProfileMergeModalComponent } from '../profile-merge-modal/profile-merge-modal.component'
import { ProfileUnclaimModalComponent } from '../profile-unclaim-modal/profile-unclaim-modal.component'

@Component({
  selector: 'app-profiles',
  templateUrl: './profiles.component.html',
  styleUrls: ['./profiles.component.scss']
})
export class ProfilesComponent implements OnInit, OnDestroy {

  @ViewChild('profileMergeModal') public profileMergeModal: ProfileMergeModalComponent
  @ViewChild('profileUnclaimModal') public profileUnclaimModal: ProfileUnclaimModalComponent

  public loading = true
  public menuOptions = {}
  public keepPersona: Persona
  public mergePersona: Persona
  public personas: Persona[] = []


  private _currentUser: User
  private _subscriptions: Subscription[] = []
  private _teamFirebaseUrl: string

  constructor(private activatedRoute: ActivatedRoute,
              private spinner: SpinnerService,
              private toast: SeFeToastService,
              private translateService: TranslateService,
              private userService: UserService,
    ) {
    // noop
  }

  public ngOnInit(): void {
    this.loading = true
    this.spinner.start()
    this.personas = this.activatedRoute.snapshot.data.personas
    this._teamFirebaseUrl = this.activatedRoute.snapshot.params.team_firebase_url
    this._subscriptions.push(this.userService.current.pipe(
      filter(user => !!user),
      tap(user => this._currentUser = user)
    ).subscribe(_ => this.loadData()))
    this.userService.getCurrent()
  }

  public ngOnDestroy(): void {
    this._subscriptions.forEach(subscription => subscription.unsubscribe())
    this._subscriptions.length = 0
  }

  public removeUnlinkedPersona(deletedId: number) {
    this.personas = this.personas.filter(p => p.id !== deletedId)
  }

  /**
   * Loads the current user profiles and an orphan invitation.
   *
   */
  public loadData() {
    this.redirect()
    this.buildPersonaMenus()
    this.loading = false
    this.spinner.stop()
  }

  public refresh() {
    window.location.reload()
  }

  public redirect() {
    if (this._teamFirebaseUrl) this.redirectToTeamPage()
  }

  public redirectToTeamPage() {
    window.location.href = this._teamFirebaseUrl
  }

  public unclaimProfileModal(persona: Persona) {
    this.profileUnclaimModal.open(persona)
  }

  public mergePersonas(keepPersona: Persona, mergePersona: Persona) {
    this.mergePersona = mergePersona
    this.keepPersona = keepPersona
    this.profileMergeModal.open()
  }

  public onSuccessfulMerge() {
    // Remove the merged persona on the client-side to avoid
    // timing issues encountered when re-fetching personas.
    // Merged persona may still be included in personas response
    // if refreshed immediately after successful merge
    this.personas = this.personas.filter(p => p.id !== this.mergePersona.id)
    this.buildPersonaMenus()
    this.toast.success(this.translateService.instant(
      'PERSONAS.merge_success',
      {
        keep: this.keepPersona.fullName,
        merge: this.mergePersona.fullName
      }
    ))
  }

  private buildPersonaMenus(): void {
    this.personas.forEach(persona => {
      this.menuOptions[persona.id] = this.createMenuOptionsFor(persona)
    })
  }

  private createMenuOptionsFor(persona: Persona) {
    // Leaving the constant below in case we ever re-enable self merges
    // const isChildPersona = !persona.meetsCopaAgeRequirement
    const editProfileSection = {
      menuItems: [
        {
        text: 'Edit Profile',
        href: `/user/profile/${persona.id}?edit=true`
        }
      ]
    }
    const mergeItems = this.personas.map(p => {
      if (p.id === persona.id || p.isSelf) return null
      return {
        text: `${p.first_name} ${p.last_name}`,
        action: () => { this.mergePersonas(p, persona) }
      }
    }).filter(x => !!x)
    const removeProfileSection = {
      menuItems: [
        {
          text: 'Remove Profile',
          action: () => { this.unclaimProfileModal(persona) }
        }
      ]
    }
    if (mergeItems.length) {
      return {
        name: `multiMenu${persona.id}`,
        sections: [
          editProfileSection,
          {
            header: 'Merge Profile With',
            divider: false,
            menuItems: mergeItems
          },
          removeProfileSection
        ]
      }
    }
    return { name: `multiMenu${persona.id}`, sections: [editProfileSection, removeProfileSection] }
  }
}
